/**
 * Typed control-api client, generated from the route table at the type
 * level (no codegen). Requests are validated before they leave; responses
 * are parsed on arrival, so a server drift is a loud client error, never
 * silent garbage in a model's context.
 *
 * Identity is per call: the MCP server passes the HUMAN's bearer token (or,
 * in dev, the DevAuth headers). The client never holds a service credential.
 */

import type { z } from 'zod';
import { routes, OPERATOR_ROUTES, STOREFRONT_ROUTES, KNOWLEDGE_ROUTES, DISCOVERY_ROUTES, type RouteName, type Routes, type OperatorRouteName, type OperatorRoutes, type StorefrontRouteName, type StorefrontRoutes, type KnowledgeRouteName, type KnowledgeRoutes, type DiscoveryRouteName, type DiscoveryRoutes } from './index.js';

export type ReqOf<N extends RouteName> = Routes[N]['request'] extends z.ZodTypeAny ? z.infer<Routes[N]['request']> : never;
export type ResOf<N extends RouteName> = z.infer<Routes[N]['response']>;

type PathParams<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? { [K in Name | keyof PathParams<`/${Rest}`>]: string }
  : P extends `${string}:${infer Name}`
    ? { [K in Name]: string }
    : Record<never, never>;
export type ParamsOf<N extends RouteName> = PathParams<Routes[N]['path']>;

export interface Identity {
  /** Cognito access token - production. */
  bearer?: string;
  /** DevAuth headers - local dev/tests. */
  devAccount?: string;
  devStaff?: { tenantId: string; role: string };
  /** DevAuth: a platform operator (dev/tests only) -> x-dev-operator. */
  devOperator?: string;
  /** DevAuth: the governance grant (dev/tests only) -> x-dev-governance, with the step-up moment -> x-dev-step-up. NOT implied by devOperator. */
  devGovernance?: string;
  devStepUpAt?: number;
  /** Multi-tenant staff: which tenant this call is for. */
  tenantHint?: string;
  /** ADR-44: the staff role this request should run as. The server verifies
   *  it against the principal's assured memberships - a browser can request
   *  a hat, never invent one. Omitted = the server's default pick. */
  activeRole?: string;
  /** Platform operator ASSUMING a tenant role for this request (audited as
   *  `assume`) -> x-act-as-role. The server requires identity.operator and
   *  rejects it otherwise, so this is a request, never authority. */
  actAsRole?: string;
  /** Set by AI agent runtimes (MCP server, agents). Tells the API the ADR-29
   *  gate applies: confirm=true requires a human approval record. */
  aiAgent?: string;
}

export interface ClientOptions {
  baseUrl: string;       // e.g. https://api.hipnum.com
  tenantSlug: string;    // the /t/:tenantSlug segment
  fetch?: typeof fetch;  // injectable (tests, Fastify inject adapters)
  /** Complain when the server sends fields the contract would strip. Defaults
   *  to ON under vitest (or CONTRACT_STRICT=1) and OFF in production, where
   *  stripping an unexpected field is the correct, safe behaviour. */
  strictResponses?: boolean;
  /** Receive drift instead of throwing - for a report rather than a failure. */
  onContractDrift?: (info: { route: string; dropped: string[]; detail: string }) => void;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: unknown, readonly route: string) {
    super(`${route} -> ${status}: ${typeof body === 'string' ? body : JSON.stringify(body)}`);
    this.name = 'ApiError';
  }
}

export interface CallOptions<N extends RouteName> {
  params?: ParamsOf<N>;
  body?: ReqOf<N>;
  identity?: Identity;
  /** Appended as the query string (undefined values dropped): the narrowing a
   *  GET route's summary promises, e.g. listReversals { registrationId }. */
  query?: Record<string, string | number | boolean | undefined>;
  /** Stops the request when aborted: a caller that must not wait forever on a link that went quiet (the fetch rejects). */
  signal?: AbortSignal;
}

export class ControlApiClient {
  private readonly f: typeof fetch;
  private readonly strict: boolean;
  constructor(private readonly opts: ClientOptions) {
    this.strict = opts.strictResponses ?? underTest();
    // Never store the global fetch unbound: calling it as a method (this.f) is
    // an "Illegal invocation" in browsers. Wrap so `this` is the window.
    this.f = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  async call<N extends RouteName>(name: N, call: CallOptions<N> = {}): Promise<ResOf<N>> {
    const def = routes[name];
    // Interpolate :params; every declared param must be supplied.
    let path: string = def.path;
    for (const [k, v] of Object.entries((call.params ?? {}) as Record<string, string>)) {
      path = path.replace(`:${k}`, encodeURIComponent(v));
    }
    if (/:[a-zA-Z]+/.test(path)) throw new Error(`${name}: missing path param in ${path}`);

    // Validate the request against the contract before it leaves.
    let bodyText: string | undefined;
    if (def.method !== 'GET') {
      const parsed = (def.request as z.ZodTypeAny).safeParse(call.body ?? {});
      if (!parsed.success) throw new Error(`${name}: invalid request - ${parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
      bodyText = JSON.stringify(parsed.data ?? {});
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (bodyText !== undefined) headers['content-type'] = 'application/json';
    const id = call.identity;
    if (id?.bearer) headers['authorization'] = 'Bearer ' + id.bearer;
    if (id?.devAccount) headers['x-dev-account'] = id.devAccount;
    if (id?.devStaff) headers['x-dev-staff'] = `${id.devStaff.tenantId}:${id.devStaff.role}`;
    if (id?.devOperator) headers['x-dev-operator'] = id.devOperator;
    if (id?.devGovernance) { headers['x-dev-governance'] = id.devGovernance; headers['x-dev-step-up'] = String(id.devStepUpAt ?? Date.now()); }
    if (id?.tenantHint) headers['x-tenant-id'] = id.tenantHint;
    if (id?.activeRole) headers['x-active-staff-role'] = id.activeRole;
    if (id?.actAsRole) headers['x-act-as-role'] = id.actAsRole;
    if (id?.aiAgent) headers['x-ai-agent'] = id.aiAgent;

    const qs = call.query
      ? Object.entries(call.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = `${this.opts.baseUrl.replace(/\/$/, '')}/t/${encodeURIComponent(this.opts.tenantSlug)}${path}${qs ? `?${qs}` : ''}`;
    const res = await this.f(url, {
      method: def.method, headers,
      ...(bodyText !== undefined ? { body: bodyText } : {}),
      ...(call.signal !== undefined ? { signal: call.signal } : {}),
    });
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, json ?? text, name);

    // Parse the response against the contract; drift is a loud error.
    const parsed = (def.response as z.ZodTypeAny).safeParse(json);
    if (!parsed.success) {
      throw new Error(`${name}: response did not match contract - ${parsed.error.issues.slice(0, 5).map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
    }
    // …and the drift that ISN'T an error: Zod strips what the contract does not
    // declare, which is correct in production (a contract that accepts anything
    // is not a contract) and invisible everywhere else. A route can be right,
    // its database can be right, its raw-HTTP test can pass, and the real
    // consumer still sees nothing - that is exactly how the ADR-40 review panel
    // shipped blank. So under test we refuse to be quiet about it.
    if (this.strict) {
      const dropped = droppedPaths(json, parsed.data);
      if (dropped.length) {
        const detail = `${name}: the server returned ${dropped.length} field(s) the contract does not declare, so a typed client never sees them: ${dropped.slice(0, 8).join(', ')}${dropped.length > 8 ? `, …` : ''}. Declare them on this route's response schema in @hipnumber/api-contract (same commit as the route change), or stop sending them.`;
        if (this.opts.onContractDrift) this.opts.onContractDrift({ route: name, dropped, detail });
        else throw new Error(detail);
      }
    }
    return parsed.data as ResOf<N>;
  }
}

// ---------------------------------------------------------------------------
// Platform operator client
//
// The operator surface has no /t/:tenantSlug prefix and no staff-role access
// model - every route requires platform-operator authority, which the server
// enforces. Same validate-before / parse-after discipline as ControlApiClient;
// identity here is the OPERATOR's (bearer in prod, x-dev-operator in dev). It
// never carries act-as: these are native operator routes, not tenant routes.
// ---------------------------------------------------------------------------

// N ranges over BOTH operator tables: PLATFORM_ROUTES (administration) and
// DIAGNOSTIC_ROUTES (observation). Merging them here is a TRANSPORT decision
// only - one client, one field-drop detector, one identity mapping. Derivation
// into MCP tools reads DIAGNOSTIC_ROUTES alone, which is what keeps
// administration underivable (ADR-49).
export type PlatformReqOf<N extends OperatorRouteName> = OperatorRoutes[N]['request'] extends z.ZodTypeAny ? z.infer<OperatorRoutes[N]['request']> : never;
export type PlatformResOf<N extends OperatorRouteName> = z.infer<OperatorRoutes[N]['response']>;
export type PlatformParamsOf<N extends OperatorRouteName> = PathParams<OperatorRoutes[N]['path']>;

export interface PlatformClientOptions {
  baseUrl: string;       // e.g. https://api.hipnum.com
  fetch?: typeof fetch;
  strictResponses?: boolean;
  onContractDrift?: (info: { route: string; dropped: string[]; detail: string }) => void;
}

export interface PlatformCallOptions<N extends OperatorRouteName> {
  params?: PlatformParamsOf<N>;
  body?: PlatformReqOf<N>;
  /** Appended as the query string (undefined values dropped). */
  query?: Record<string, string | number | boolean | undefined>;
  identity?: Identity;
}

export class PlatformApiClient {
  private readonly f: typeof fetch;
  private readonly strict: boolean;
  constructor(private readonly opts: PlatformClientOptions) {
    this.strict = opts.strictResponses ?? underTest();
    this.f = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  async call<N extends OperatorRouteName>(name: N, call: PlatformCallOptions<N> = {}): Promise<PlatformResOf<N>> {
    const def = OPERATOR_ROUTES[name];
    let path: string = def.path;
    for (const [k, v] of Object.entries((call.params ?? {}) as Record<string, string>)) {
      path = path.replace(`:${k}`, encodeURIComponent(v));
    }
    if (/:[a-zA-Z]+/.test(path)) throw new Error(`${name}: missing path param in ${path}`);

    let bodyText: string | undefined;
    if (def.method !== 'GET') {
      const parsed = (def.request as z.ZodTypeAny).safeParse(call.body ?? {});
      if (!parsed.success) throw new Error(`${name}: invalid request - ${parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
      bodyText = JSON.stringify(parsed.data ?? {});
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (bodyText !== undefined) headers['content-type'] = 'application/json';
    const id = call.identity;
    if (id?.bearer) headers['authorization'] = 'Bearer ' + id.bearer;
    if (id?.devOperator) headers['x-dev-operator'] = id.devOperator;
    if (id?.devGovernance) { headers['x-dev-governance'] = id.devGovernance; headers['x-dev-step-up'] = String(id.devStepUpAt ?? Date.now()); }
    if (id?.devAccount) headers['x-dev-account'] = id.devAccount;

    const qs = call.query
      ? Object.entries(call.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = `${this.opts.baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;
    const res = await this.f(url, { method: def.method, headers, ...(bodyText !== undefined ? { body: bodyText } : {}) });
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, json ?? text, name);

    const parsed = (def.response as z.ZodTypeAny).safeParse(json);
    if (!parsed.success) {
      throw new Error(`${name}: response did not match contract - ${parsed.error.issues.slice(0, 5).map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
    }
    if (this.strict) {
      const dropped = droppedPaths(json, parsed.data);
      if (dropped.length) {
        const detail = `${name}: the server returned ${dropped.length} field(s) the contract does not declare, so a typed client never sees them: ${dropped.slice(0, 8).join(', ')}${dropped.length > 8 ? `, …` : ''}. Declare them on this route's response schema in @hipnumber/api-contract, or stop sending them.`;
        if (this.opts.onContractDrift) this.opts.onContractDrift({ route: name, dropped, detail });
        else throw new Error(detail);
      }
    }
    return parsed.data as PlatformResOf<N>;
  }
}

/**
 * Paths present in what the server sent but absent from what the contract kept.
 *
 * Only plain objects are walked: a `z.record`/`z.unknown()` field keeps whatever
 * it was given, so nothing is reported there - the check fires precisely where a
 * declared `z.object` silently dropped something.
 */
export function droppedPaths(raw: unknown, kept: unknown, at = ''): string[] {
  if (raw === null || typeof raw !== 'object') return [];
  if (Array.isArray(raw)) {
    if (!Array.isArray(kept)) return [];
    // One element is enough to prove the shape; reporting all N is noise.
    return raw.length && kept.length ? droppedPaths(raw[0], kept[0], `${at}[]`) : [];
  }
  if (kept === null || typeof kept !== 'object' || Array.isArray(kept)) return [];
  const out: string[] = [];
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === undefined) continue;
    const path = at ? `${at}.${k}` : k;
    if (!(k in (kept as Record<string, unknown>))) out.push(path);
    else out.push(...droppedPaths(v, (kept as Record<string, unknown>)[k], path));
  }
  return out;
}

/** True when we are inside a test run, so the check needs no configuration. */
function underTest(): boolean {
  try {
    return typeof process !== 'undefined' && Boolean(process.env?.['VITEST'] ?? process.env?.['CONTRACT_STRICT']);
  } catch { return false; }
}

function safeJson(t: string): unknown {
  try { return JSON.parse(t); } catch { return t; }
}

/** Adapter so tests (and in-process callers) can drive the client through
 *  Fastify's inject() without opening a socket. */
export function fetchViaInject(app: { inject: (o: { method: string; url: string; headers?: Record<string, string>; payload?: string }) => Promise<{ statusCode: number; body: string; headers: Record<string, unknown> }> }): typeof fetch {
  return (async (input: string | URL | Request, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const u = new URL(url);
    const res = await app.inject({
      method: (init?.method ?? 'GET').toUpperCase(),
      url: u.pathname + u.search,
      headers: (init?.headers as Record<string, string>) ?? {},
      ...(init?.body ? { payload: String(init.body) } : {}),
    });
    return new Response(res.body, { status: res.statusCode, headers: { 'content-type': 'application/json' } });
  }) as typeof fetch;
}

// ---------------------------------------------------------------------------
// The storefront client (ADR-54): anonymous, host-keyed, GET only.
// ---------------------------------------------------------------------------

type StorefrontResOf<N extends StorefrontRouteName> = z.infer<StorefrontRoutes[N]['response']>;
type StorefrontParamsOf<N extends StorefrontRouteName> = PathParams<StorefrontRoutes[N]['path']>;

export interface StorefrontClientOptions {
  baseUrl: string;
  fetch?: typeof fetch;
  strictResponses?: boolean;
  onContractDrift?: (info: { route: string; dropped: string[]; detail: string }) => void;
}

export interface StorefrontCallOptions<N extends StorefrontRouteName> {
  params: StorefrontParamsOf<N>;
  query?: Record<string, string | number | boolean | undefined>;
}

/**
 * No identity, ever: the storefront is what an anonymous visitor sees, and a
 * client that could carry a bearer token would eventually be used to fetch
 * something a visitor cannot. The host is a path param like any other and
 * is URL-encoded on the way out, so a request host is never spliced raw.
 */
export class StorefrontClient {
  private readonly f: typeof fetch;
  private readonly strict: boolean;
  constructor(private readonly opts: StorefrontClientOptions) {
    this.strict = opts.strictResponses ?? underTest();
    this.f = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  async call<N extends StorefrontRouteName>(name: N, call: StorefrontCallOptions<N>): Promise<StorefrontResOf<N>> {
    const def = STOREFRONT_ROUTES[name];
    let path: string = def.path;
    for (const [k, v] of Object.entries(call.params as Record<string, string>)) {
      path = path.replace(`:${k}`, encodeURIComponent(v));
    }
    if (/:[a-zA-Z]+/.test(path)) throw new Error(`${name}: missing path param in ${path}`);
    const qs = call.query
      ? Object.entries(call.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = `${this.opts.baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;
    const res = await this.f(url, { method: 'GET', headers: { accept: 'application/json' } });
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, json ?? text, name);
    const parsed = (def.response as z.ZodTypeAny).safeParse(json);
    if (!parsed.success) {
      throw new Error(`${name}: response did not match contract - ${parsed.error.issues.slice(0, 5).map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
    }
    if (this.strict) {
      const dropped = droppedPaths(json, parsed.data);
      if (dropped.length) {
        const detail = `${name}: the server returned ${dropped.length} field(s) the contract does not declare, so a typed client never sees them: ${dropped.slice(0, 8).join(', ')}${dropped.length > 8 ? ', ...' : ''}. Declare them on this route's response schema in @hipnumber/api-contract, or stop sending them.`;
        if (this.opts.onContractDrift) this.opts.onContractDrift({ route: name, dropped, detail });
        else throw new Error(detail);
      }
    }
    return parsed.data as StorefrontResOf<N>;
  }
}

// ---------------------------------------------------------------------------
// The knowledge client (Knowledge Base V1): the fifth route table.
//
// Absolute paths (no /t/:tenantSlug) and two kinds of caller: a company's staff
// (reads) and the platform operator (reads and writes). Identity is per call
// and maps like the other clients; `tenantHint` picks which company a
// multi-company staffer is reading FOR (x-tenant-id) - it never grants
// anything, the server resolves staff from the verified token. `actAsRole`
// (x-act-as-role) is an operator opening a company's console: the Help pages
// there are then read as that company would read them, never as the operator
// (the header can only ever narrow what the server returns). `aiAgent` marks
// a call that came through the MCP server, which is what makes the API record
// the author as AI and apply the agent rules (draft only, no deleting a
// published article).
// ---------------------------------------------------------------------------

export type KnowledgeReqOf<N extends KnowledgeRouteName> = KnowledgeRoutes[N]['request'] extends z.ZodTypeAny ? z.infer<KnowledgeRoutes[N]['request']> : never;
export type KnowledgeResOf<N extends KnowledgeRouteName> = z.infer<KnowledgeRoutes[N]['response']>;
export type KnowledgeParamsOf<N extends KnowledgeRouteName> = PathParams<KnowledgeRoutes[N]['path']>;

export interface KnowledgeClientOptions {
  baseUrl: string;       // e.g. https://api.hipnum.com
  fetch?: typeof fetch;
  strictResponses?: boolean;
  onContractDrift?: (info: { route: string; dropped: string[]; detail: string }) => void;
}

export interface KnowledgeCallOptions<N extends KnowledgeRouteName> {
  params?: KnowledgeParamsOf<N>;
  body?: KnowledgeReqOf<N>;
  /** Appended as the query string (undefined values dropped). */
  query?: Record<string, string | number | boolean | undefined>;
  identity?: Identity;
}

export class KnowledgeApiClient {
  private readonly f: typeof fetch;
  private readonly strict: boolean;
  constructor(private readonly opts: KnowledgeClientOptions) {
    this.strict = opts.strictResponses ?? underTest();
    this.f = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  async call<N extends KnowledgeRouteName>(name: N, call: KnowledgeCallOptions<N> = {}): Promise<KnowledgeResOf<N>> {
    const def = KNOWLEDGE_ROUTES[name];
    let path: string = def.path;
    for (const [k, v] of Object.entries((call.params ?? {}) as Record<string, string>)) {
      path = path.replace(`:${k}`, encodeURIComponent(v));
    }
    if (/:[a-zA-Z]+/.test(path)) throw new Error(`${name}: missing path param in ${path}`);

    let bodyText: string | undefined;
    if (def.method !== 'GET') {
      const parsed = (def.request as z.ZodTypeAny).safeParse(call.body ?? {});
      if (!parsed.success) throw new Error(`${name}: invalid request - ${parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
      bodyText = JSON.stringify(parsed.data ?? {});
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (bodyText !== undefined) headers['content-type'] = 'application/json';
    const id = call.identity;
    if (id?.bearer) headers['authorization'] = 'Bearer ' + id.bearer;
    if (id?.devAccount) headers['x-dev-account'] = id.devAccount;
    if (id?.devStaff) headers['x-dev-staff'] = `${id.devStaff.tenantId}:${id.devStaff.role}`;
    if (id?.devOperator) headers['x-dev-operator'] = id.devOperator;
    if (id?.tenantHint) headers['x-tenant-id'] = id.tenantHint;
    if (id?.actAsRole) headers['x-act-as-role'] = id.actAsRole;
    if (id?.aiAgent) headers['x-ai-agent'] = id.aiAgent;

    const qs = call.query
      ? Object.entries(call.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = `${this.opts.baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;
    const res = await this.f(url, { method: def.method, headers, ...(bodyText !== undefined ? { body: bodyText } : {}) });
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, json ?? text, name);

    const parsed = (def.response as z.ZodTypeAny).safeParse(json);
    if (!parsed.success) {
      throw new Error(`${name}: response did not match contract - ${parsed.error.issues.slice(0, 5).map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
    }
    if (this.strict) {
      const dropped = droppedPaths(json, parsed.data);
      if (dropped.length) {
        const detail = `${name}: the server returned ${dropped.length} field(s) the contract does not declare, so a typed client never sees them: ${dropped.slice(0, 8).join(', ')}${dropped.length > 8 ? ', ...' : ''}. Declare them on this route's response schema in @hipnumber/api-contract, or stop sending them.`;
        if (this.opts.onContractDrift) this.opts.onContractDrift({ route: name, dropped, detail });
        else throw new Error(detail);
      }
    }
    return parsed.data as KnowledgeResOf<N>;
  }
}

// -- Discovery directory (ADR-71) -------------------------------------------
// The sixth table, DISCOVERY_ROUTES: the review desk in cut 1, the public
// directory reads later. Same transport as the Knowledge client: absolute
// paths, per-call identity, strict responses under test.

export type DiscoveryReqOf<N extends DiscoveryRouteName> = DiscoveryRoutes[N]['request'] extends z.ZodTypeAny ? z.infer<DiscoveryRoutes[N]['request']> : never;
export type DiscoveryResOf<N extends DiscoveryRouteName> = z.infer<DiscoveryRoutes[N]['response']>;
export type DiscoveryParamsOf<N extends DiscoveryRouteName> = PathParams<DiscoveryRoutes[N]['path']>;

export interface DiscoveryClientOptions {
  baseUrl: string;       // e.g. https://api.hipnum.com
  fetch?: typeof fetch;
  strictResponses?: boolean;
  onContractDrift?: (info: { route: string; dropped: string[]; detail: string }) => void;
}

export interface DiscoveryCallOptions<N extends DiscoveryRouteName> {
  params?: DiscoveryParamsOf<N>;
  body?: DiscoveryReqOf<N>;
  /** Appended as the query string (undefined values dropped). */
  query?: Record<string, string | number | boolean | undefined>;
  identity?: Identity;
}

export class DiscoveryApiClient {
  private readonly f: typeof fetch;
  private readonly strict: boolean;
  constructor(private readonly opts: DiscoveryClientOptions) {
    this.strict = opts.strictResponses ?? underTest();
    this.f = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  async call<N extends DiscoveryRouteName>(name: N, call: DiscoveryCallOptions<N> = {}): Promise<DiscoveryResOf<N>> {
    const def = DISCOVERY_ROUTES[name];
    let path: string = def.path;
    for (const [k, v] of Object.entries((call.params ?? {}) as Record<string, string>)) {
      path = path.replace(`:${k}`, encodeURIComponent(v));
    }
    if (/:[a-zA-Z]+/.test(path)) throw new Error(`${name}: missing path param in ${path}`);

    let bodyText: string | undefined;
    if (def.method !== 'GET') {
      const parsed = (def.request as z.ZodTypeAny).safeParse(call.body ?? {});
      if (!parsed.success) throw new Error(`${name}: invalid request - ${parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
      bodyText = JSON.stringify(parsed.data ?? {});
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (bodyText !== undefined) headers['content-type'] = 'application/json';
    const id = call.identity;
    if (id?.bearer) headers['authorization'] = 'Bearer ' + id.bearer;
    if (id?.devAccount) headers['x-dev-account'] = id.devAccount;
    if (id?.devStaff) headers['x-dev-staff'] = `${id.devStaff.tenantId}:${id.devStaff.role}`;
    if (id?.devOperator) headers['x-dev-operator'] = id.devOperator;
    if (id?.tenantHint) headers['x-tenant-id'] = id.tenantHint;
    if (id?.actAsRole) headers['x-act-as-role'] = id.actAsRole;
    if (id?.aiAgent) headers['x-ai-agent'] = id.aiAgent;

    const qs = call.query
      ? Object.entries(call.query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const url = `${this.opts.baseUrl.replace(/\/$/, '')}${path}${qs ? `?${qs}` : ''}`;
    const res = await this.f(url, { method: def.method, headers, ...(bodyText !== undefined ? { body: bodyText } : {}) });
    const text = await res.text();
    const json: unknown = text ? safeJson(text) : undefined;
    if (!res.ok) throw new ApiError(res.status, json ?? text, name);

    const parsed = (def.response as z.ZodTypeAny).safeParse(json);
    if (!parsed.success) {
      throw new Error(`${name}: response did not match contract - ${parsed.error.issues.slice(0, 5).map((i) => i.path.join('.') + ' ' + i.message).join('; ')}`);
    }
    if (this.strict) {
      const dropped = droppedPaths(json, parsed.data);
      if (dropped.length) {
        const detail = `${name}: the server returned ${dropped.length} field(s) the contract does not declare, so a typed client never sees them: ${dropped.slice(0, 8).join(', ')}${dropped.length > 8 ? ', ...' : ''}. Declare them on this route's response schema in @hipnumber/api-contract, or stop sending them.`;
        if (this.opts.onContractDrift) this.opts.onContractDrift({ route: name, dropped, detail });
        else throw new Error(detail);
      }
    }
    return parsed.data as DiscoveryResOf<N>;
  }
}
